import React from 'react';

import { CreateUpdateQuestionDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  QuestionResponseTypeObj,
  YesNoObj,
} from '../../../common/pokCore/validation/enums';
import { usePokCore } from '../../../common/hooks/usePokCore';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import {
  SortableList,
  SortableListColumn,
} from '../../../common/components/SortableList/SortableList';

interface SurveyQuestionsGridProps {
  questions: CreateUpdateQuestionDto[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  isLocked?: boolean;
}

export const SurveyQuestionsGrid: React.FC<SurveyQuestionsGridProps> = ({
  questions,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  isLocked,
}) => {
  const { t, tk } = useTranslation('administrationLng');
  const pok = usePokCore();
  const lang = pok.getLanguage();

  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const columns: SortableListColumn<CreateUpdateQuestionDto>[] = [
    {
      header: t(tk.common.lp),
      render: (_item, index) => index + 1,
      width: 50,
    },
    {
      header: t(tk.survey.question),
      render: item =>
        stripHtmlTags(lang === 'PL' ? item.question : item.questionEn || ''),
    },
    {
      header: t(tk.survey.responseType),
      render: item => t(QuestionResponseTypeObj[item.responseType]),
    },
    {
      header: t(tk.survey.optional),
      render: item => (item.optional ? t(tk.common.yes) : t(tk.common.no)),
    },
    {
      header: t(tk.survey.expectedAnswer),
      render: item =>
        item.expectedYesNoAnswer ? t(YesNoObj[item.expectedYesNoAnswer]) : '-',
    },
    {
      header: t(tk.survey.blockOnWrongAnswerShort),
      render: item =>
        item.blockOnWrongAnswer ? t(tk.common.yes) : t(tk.common.no),
    },
    {
      header: t(tk.survey.warningLight),
      render: item => (item.warningLight ? t(tk.common.yes) : t(tk.common.no)),
    },
  ];

  return (
    <SortableList
      items={questions}
      columns={columns}
      onEdit={isLocked ? undefined : onEdit}
      onDelete={isLocked ? undefined : onDelete}
      onMoveUp={isLocked ? undefined : onMoveUp}
      onMoveDown={isLocked ? undefined : onMoveDown}
      emptyMessage={t(tk.survey.noQuestions)}
      editTooltip={t(tk.survey.questionEdit)}
      deleteTooltip={t(tk.survey.questionDelete)}
      deleteConfirmation={t(tk.survey.questionDeleteConfirm)}
      moveUpTooltip={t(tk.survey.moveQuestionUp)}
      moveDownTooltip={t(tk.survey.moveQuestionDown)}
      renderRowExtra={item =>
        item.id ? (
          <LastEditorInfo
            id={item.id}
            method={pok.surveys.getQuestionLastEditor}
            className="last-editor-inline"
            placement="left"
          />
        ) : null
      }
    />
  );
};
